/**
 * Chunk 加载错误处理器
 * 捕获由于版本更新导致的 JS 文件加载失败，自动刷新页面
 */

/**
 * 判断是否为 Chunk 加载错误
 * @param {Error} error 
 * @returns {boolean}
 */
function isChunkLoadError(error) {
  if (!error) return false

  const errorString = error.toString().toLowerCase()
  const errorMessage = (error.message || '').toLowerCase()
  const errorName = (error.name || '').toLowerCase()

  // ChunkLoadError: Webpack 动态导入失败
  if (errorName === 'chunkloaderror') return true

  // Loading chunk xxx failed
  if (errorMessage.includes('loading chunk') && errorMessage.includes('failed')) return true

  // Loading CSS chunk xxx failed
  if (errorMessage.includes('loading css chunk') && errorMessage.includes('failed')) return true

  // Unexpected token '<' - 通常是请求 JS 文件返回了 HTML（404 页面）
  if (errorMessage.includes('unexpected token') && errorMessage.includes('<')) return true

  // Failed to fetch dynamically imported module
  if (errorMessage.includes('failed to fetch dynamically imported module')) return true

  // 网络错误导致的资源加载失败
  if (errorMessage.includes('failed to load') || errorString.includes('networkerror')) return true

  // Syntax error in module - 可能是加载了错误版本的文件
  if (errorMessage.includes('syntaxerror') && errorMessage.includes('module')) return true

  return false
}

/**
 * 执行安全刷新
 * @param {string} reason 刷新原因
 */
function performSafeReload(reason) {
  console.warn('[ChunkErrorHandler] 检测到资源加载错误，准备刷新页面:', reason)

  // 防止刷新循环：记录刷新时间
  const lastReloadKey = 'chunk_error_last_reload'
  const lastReload = sessionStorage.getItem(lastReloadKey)
  const now = Date.now()

  if (lastReload && now - parseInt(lastReload) < 10000) {
    // 10 秒内已经刷新过，不再刷新，避免死循环
    console.error('[ChunkErrorHandler] 短时间内多次刷新，停止自动刷新以避免循环')
    return
  }

  sessionStorage.setItem(lastReloadKey, now.toString())

  // 强制刷新，绕过缓存
  window.location.reload(true)
}

/**
 * 安装全局错误处理器
 * @param {Vue} Vue Vue 构造函数
 */
export function installChunkErrorHandler(Vue) {
  // Vue 错误处理器 - 捕获组件渲染和生命周期钩子中的错误
  Vue.config.errorHandler = (error, vm, info) => {
    console.error('[Vue Error]', error, info)

    if (isChunkLoadError(error)) {
      performSafeReload(error.message)
      return
    }

    // 非 Chunk 错误，继续抛出让开发者看到
    throw error
  }

  // 全局 unhandledrejection - 捕获未处理的 Promise 拒绝
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason

    if (isChunkLoadError(error)) {
      event.preventDefault()
      performSafeReload((error && error.message) || 'Promise rejection')
    }
  })

  // 全局 error 事件 - 捕获脚本加载错误
  window.addEventListener('error', (event) => {
    // 检查是否为资源加载错误（script/link 标签）
    const target = event.target
    if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      const src = target.src || target.href || ''
      // 只处理应用内的资源加载失败
      if (src.includes('/js/') || src.includes('/css/')) {
        console.warn('[ChunkErrorHandler] 资源加载失败:', src)
        performSafeReload(`资源加载失败: ${src}`)
        return
      }
    }

    // 检查是否为 Chunk 加载错误
    if (event.error && isChunkLoadError(event.error)) {
      performSafeReload(event.error.message)
    }
  }, true) // 使用捕获阶段，确保能捕获到资源加载错误

  console.log('[ChunkErrorHandler] 全局错误处理器已安装')
}

export default installChunkErrorHandler
